<script setup lang="ts">
import { ref, computed } from 'vue'
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppLogo from '@/components/common/AppLogo.vue'
import {
  HomeIcon,
  ShoppingBagIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  PlayCircleIcon,
  TicketIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  NewspaperIcon,
} from '@heroicons/vue/24/outline'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const sidebarOpen = ref(false)

const menuItems = [
  {
    name: 'Dashboard',
    icon: HomeIcon,
    route: 'admin-dashboard',
    description: 'Visão geral'
  },
  {
    name: 'Produtos',
    icon: ShoppingBagIcon,
    route: 'admin-products',
    description: 'Gerenciar produtos'
  },
  {
    name: 'Pedidos',
    icon: ClipboardDocumentListIcon,
    route: 'admin-orders',
    description: 'Gerenciar pedidos'
  },
  {
    name: 'Clientes',
    icon: UsersIcon,
    route: 'admin-users',
    description: 'Gerenciar usuários'
  },
  {
    name: 'Vídeos',
    icon: PlayCircleIcon,
    route: 'admin-videos',
    description: 'Gerenciar vídeos'
  },
  {
    name: 'Notícias',
    icon: NewspaperIcon,
    route: 'admin-news',
    description: 'Matérias e eventos'
  },
  {
    name: 'Cupons',
    icon: TicketIcon,
    route: 'admin-coupons',
    description: 'Gerenciar cupons'
  },
  {
    name: 'Canais de Denúncia',
    icon: MegaphoneIcon,
    route: 'admin-channels',
    description: 'Gerenciar canais'
  },
  {
    name: 'Financeiro',
    icon: CurrencyDollarIcon,
    route: 'admin-financial',
    description: 'Relatórios financeiros'
  },
  {
    name: 'Configurações',
    icon: Cog6ToothIcon,
    route: 'admin-settings',
    description: 'Configurações do site'
  },
]

const currentRouteName = computed(() => route.name)

async function handleLogout() {
  await authStore.signOut()
  router.push({ name: 'home' })
}

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
}

function closeSidebar() {
  sidebarOpen.value = false
}
</script>

<template>
  <div class="min-h-screen bg-gray-100 lg:flex">
    <!-- Mobile sidebar backdrop -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-30 w-72 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:inset-auto lg:flex-shrink-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <div class="h-full flex flex-col">
        <!-- Logo -->
        <div class="flex items-center justify-between px-5 py-5 border-b border-gray-800">
          <RouterLink to="/admin" class="flex items-center gap-3">
            <AppLogo variant="white" size="xs" />
            <span class="text-xs text-gray-400 border-l border-gray-700 pl-3">Admin</span>
          </RouterLink>
          <button
            class="lg:hidden p-2 text-gray-400 hover:text-white"
            @click="closeSidebar"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- User info -->
        <div class="p-4 border-b border-gray-800">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
              <span class="text-white font-semibold">
                {{ authStore.fullName?.charAt(0).toUpperCase() }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-white truncate">
                {{ authStore.fullName }}
              </p>
              <p class="text-xs text-gray-400 truncate">
                Administrador
              </p>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
          <RouterLink
            v-for="item in menuItems"
            :key="item.route"
            :to="{ name: item.route }"
            :class="[
              'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200',
              currentRouteName === item.route || (route.name as string)?.startsWith(item.route.replace('admin-', 'admin-'))
                ? 'bg-primary-600 text-white'
                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
            ]"
            @click="closeSidebar"
          >
            <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
            <div class="flex-1 min-w-0">
              <span class="block font-medium">{{ item.name }}</span>
              <span class="block text-xs opacity-70">{{ item.description }}</span>
            </div>
          </RouterLink>
        </nav>

        <!-- Footer links -->
        <div class="p-4 border-t border-gray-800 space-y-2">
          <RouterLink
            to="/"
            class="flex items-center gap-3 px-4 py-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <span class="text-sm">← Voltar ao site</span>
          </RouterLink>
          <button
            @click="handleLogout"
            class="flex items-center gap-3 w-full px-4 py-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowLeftOnRectangleIcon class="w-5 h-5" />
            <span class="text-sm">Sair</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Main content -->
    <div class="flex-1 min-w-0 flex flex-col">
      <!-- Top bar -->
      <header class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-10">
        <div class="flex items-center gap-4">
          <button
            class="lg:hidden p-2 text-gray-500 hover:text-gray-700"
            @click="toggleSidebar"
          >
            <Bars3Icon class="w-6 h-6" />
          </button>
          <h1 class="text-lg font-semibold text-gray-900 hidden sm:block">
            Painel Administrativo
          </h1>
        </div>

        <div class="flex items-center gap-4">
          <RouterLink
            to="/cliente"
            class="text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            Área do Cliente →
          </RouterLink>
        </div>
      </header>

      <!-- Page content -->
      <main class="flex-1 p-4 lg:p-8 overflow-y-auto">
        <RouterView />
      </main>
    </div>
  </div>
</template>
